/**
 * Product image magnify
 * - Desktop: click to zoom in-frame (smooth), move mouse to explore, leave/click to close
 * - Mobile: tap to zoom (smooth), drag to pan (gallery locked while zoomed).
 *   A quick horizontal swipe still advances to the next/previous photo.
 */
(function () {
  const ZOOM_RATIO = 2.4;
  const ZOOM_MS = 340;
  const ZOOM_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  let activeOverlay = null;
  let galleryLock = null;
  let closeTimer = null;
  let isClosing = false;
  /** After closing zoom, ignore the click browsers retarget onto the image underneath. */
  let suppressImageClickUntil = 0;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function toggleLoadingSpinner(image, show) {
    const spinner = image.parentElement?.parentElement?.querySelector('.loading__spinner');
    if (!spinner) return;
    spinner.classList.toggle('hidden', !show);
  }

  function imageUrl(image) {
    return image.currentSrc || image.src;
  }

  function findGallerySlider(image) {
    return (
      image.closest('[id^="Slider-Gallery"]') ||
      image.closest('.product__media-list') ||
      image.closest('ul.slider') ||
      image.closest('.slider')
    );
  }

  function lockGallerySwipe(image) {
    unlockGallerySwipe();
    const slider = findGallerySlider(image);
    if (!slider) return;

    galleryLock = {
      el: slider,
      overflowX: slider.style.overflowX,
      overflowY: slider.style.overflowY,
      overflow: slider.style.overflow,
      touchAction: slider.style.touchAction,
      scrollLeft: slider.scrollLeft,
    };

    slider.style.overflow = 'hidden';
    slider.style.overflowX = 'hidden';
    slider.style.touchAction = 'none';
    slider.classList.add('is-zoom-locked');
    slider.scrollLeft = galleryLock.scrollLeft;

    galleryLock.onScroll = () => {
      if (galleryLock) slider.scrollLeft = galleryLock.scrollLeft;
    };
    slider.addEventListener('scroll', galleryLock.onScroll, { passive: false });
  }

  function unlockGallerySwipe() {
    if (!galleryLock) return;
    const { el, overflow, overflowX, overflowY, touchAction, scrollLeft, onScroll } = galleryLock;
    if (onScroll) el.removeEventListener('scroll', onScroll);
    el.style.overflow = overflow;
    el.style.overflowX = overflowX;
    el.style.overflowY = overflowY;
    el.style.touchAction = touchAction;
    el.classList.remove('is-zoom-locked');
    el.scrollLeft = scrollLeft;
    galleryLock = null;
  }

  function setZoomTransition(overlay, enabled) {
    if (!overlay) return;
    if (!enabled || prefersReducedMotion()) {
      overlay.style.transition = 'none';
      return;
    }
    overlay.style.transition = `background-size ${ZOOM_MS}ms ${ZOOM_EASING}, background-position ${ZOOM_MS}ms ${ZOOM_EASING}`;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function applyPosition(overlay, image, posX, posY, ratio = ZOOM_RATIO) {
    const width = Math.max(image.clientWidth, 1) * ratio;
    overlay.style.backgroundSize = `${width}px auto`;
    overlay.style.backgroundPosition = `${posX}% ${posY}%`;
  }

  function pointToPercent(el, clientX, clientY) {
    const rect = el.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width);
    const y = clamp(clientY - rect.top, 0, rect.height);
    return {
      x: rect.width ? (x / rect.width) * 100 : 50,
      y: rect.height ? (y / rect.height) * 100 : 50,
    };
  }

  function removeOverlay({ animate = true } = {}) {
    if (!activeOverlay) return;
    if (isClosing && animate) return;

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    const overlay = activeOverlay;
    const image = overlay._magnifyImage;
    const pos = overlay._magnifyPos || { x: 50, y: 50 };

    const finish = () => {
      unlockGallerySwipe();
      overlay.remove();
      if (activeOverlay === overlay) activeOverlay = null;
      isClosing = false;
      closeTimer = null;
      suppressImageClickUntil = performance.now() + 450;
    };

    const canAnimate = animate && !prefersReducedMotion() && image;

    if (!canAnimate) {
      activeOverlay = null;
      isClosing = false;
      finish();
      return;
    }

    isClosing = true;
    setZoomTransition(overlay, true);
    applyPosition(overlay, image, pos.x, pos.y, 1);

    closeTimer = setTimeout(finish, ZOOM_MS + 20);
  }

  function animateZoomIn(overlay, image, posX, posY) {
    overlay._magnifyPos = { x: posX, y: posY };

    if (prefersReducedMotion()) {
      applyPosition(overlay, image, posX, posY, ZOOM_RATIO);
      return;
    }

    setZoomTransition(overlay, false);
    applyPosition(overlay, image, posX, posY, 1);
    // Force layout so the 1× frame paints before we ease to zoom.
    void overlay.offsetWidth;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (activeOverlay !== overlay || isClosing) return;
        setZoomTransition(overlay, true);
        applyPosition(overlay, image, posX, posY, ZOOM_RATIO);
        closeTimer = setTimeout(() => {
          if (activeOverlay !== overlay || isClosing) return;
          setZoomTransition(overlay, false);
          closeTimer = null;
        }, ZOOM_MS + 20);
      });
    });
  }

  function createOverlay(image) {
    removeOverlay({ animate: false });
    suppressImageClickUntil = 0;
    isClosing = false;

    const overlay = document.createElement('div');
    overlay.className = 'image-magnify-full-size';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.backgroundImage = `url("${imageUrl(image)}")`;
    overlay.style.backgroundColor = 'var(--gradient-background)';
    overlay.style.backgroundRepeat = 'no-repeat';
    overlay.style.touchAction = 'none';
    overlay.style.cursor = 'zoom-out';
    overlay.style.userSelect = 'none';
    overlay.style.webkitUserSelect = 'none';
    overlay.style.zIndex = '10';
    overlay.style.willChange = 'background-size, background-position';
    overlay._magnifyImage = image;

    if (image.parentElement) {
      image.parentElement.insertBefore(overlay, image);
    }

    toggleLoadingSpinner(image, true);
    const done = () => toggleLoadingSpinner(image, false);
    const probe = new Image();
    probe.onload = done;
    probe.onerror = done;
    probe.src = imageUrl(image);
    if (probe.complete) done();

    activeOverlay = overlay;
    return overlay;
  }

  function bindDesktop(overlay, image, startEvent) {
    let pos = pointToPercent(overlay, startEvent.clientX, startEvent.clientY);
    animateZoomIn(overlay, image, pos.x, pos.y);

    overlay.addEventListener('mousemove', (event) => {
      if (isClosing) return;
      pos = pointToPercent(overlay, event.clientX, event.clientY);
      overlay._magnifyPos = pos;
      applyPosition(overlay, image, pos.x, pos.y);
    });
    overlay.addEventListener('mouseleave', () => removeOverlay({ animate: true }));
    const closeZoom = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      removeOverlay({ animate: true });
    };
    overlay.addEventListener('pointerup', closeZoom);
    overlay.addEventListener('click', closeZoom);
  }

  function goGallerySlide(image, direction) {
    const component = image.closest('slider-component, media-gallery') || image.closest('.product__media-wrapper');
    const root = component || document;
    const button =
      direction > 0
        ? root.querySelector('button[name="next"]')
        : root.querySelector('button[name="previous"]');

    if (button && !button.hasAttribute('disabled')) {
      button.click();
      return;
    }

    const slider = findGallerySlider(image);
    if (!slider) return;
    const slides = Array.from(slider.querySelectorAll('.slider__slide, .product__media-item, [id^="Slide-"]'));
    if (slides.length < 2) return;
    const step = slides[1].offsetLeft - slides[0].offsetLeft || slider.clientWidth;
    slider.scrollTo({
      left: slider.scrollLeft + direction * step,
      behavior: 'smooth',
    });
  }

  function bindTouchPan(overlay, image, startEvent) {
    lockGallerySwipe(image);

    let pos = pointToPercent(overlay, startEvent.clientX, startEvent.clientY);
    animateZoomIn(overlay, image, pos.x, pos.y);

    let dragging = false;
    let moved = false;
    let lastX = 0;
    let lastY = 0;
    let startX = 0;
    let startY = 0;
    let startTime = 0;

    overlay.style.cursor = 'grab';

    const stop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    };

    const panFromDelta = (dx, dy) => {
      const rect = overlay.getBoundingClientRect();
      const extraX = Math.max(rect.width * (ZOOM_RATIO - 1), 1);
      const extraY = Math.max(rect.height * (ZOOM_RATIO - 1), 1);
      pos.x = clamp(pos.x - (dx / extraX) * 100, 0, 100);
      pos.y = clamp(pos.y - (dy / extraY) * 100, 0, 100);
      overlay._magnifyPos = pos;
      applyPosition(overlay, image, pos.x, pos.y);
      if (galleryLock) galleryLock.el.scrollLeft = galleryLock.scrollLeft;
    };

    const begin = (clientX, clientY, event) => {
      if (isClosing) return;
      dragging = true;
      moved = false;
      lastX = clientX;
      lastY = clientY;
      startX = clientX;
      startY = clientY;
      startTime = performance.now();
      overlay.style.cursor = 'grabbing';
      setZoomTransition(overlay, false);
      if (event) stop(event);
    };

    const move = (clientX, clientY, event) => {
      if (!dragging || isClosing) return;
      const dx = clientX - lastX;
      const dy = clientY - lastY;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) moved = true;
      lastX = clientX;
      lastY = clientY;
      panFromDelta(dx, dy);
      if (event) stop(event);
    };

    const end = (event) => {
      if (!dragging) return;
      dragging = false;
      overlay.style.cursor = 'grab';

      const totalDx = lastX - startX;
      const totalDy = lastY - startY;
      const dt = Math.max(performance.now() - startTime, 1);
      const absX = Math.abs(totalDx);
      const absY = Math.abs(totalDy);
      const velocity = absX / dt;
      const isQuickSwipe =
        moved &&
        dt < 340 &&
        absX >= 46 &&
        absX > absY * 1.2 &&
        velocity >= 0.35;

      if (isQuickSwipe) {
        stop(event);
        const direction = totalDx < 0 ? 1 : -1;
        removeOverlay({ animate: true });
        setTimeout(() => goGallerySlide(image, direction), ZOOM_MS);
        return;
      }

      if (!moved) {
        stop(event);
        removeOverlay({ animate: true });
        return;
      }

      event.stopPropagation();
    };

    overlay.addEventListener('pointerdown', (event) => {
      try {
        overlay.setPointerCapture(event.pointerId);
      } catch (_) {}
      begin(event.clientX, event.clientY, event);
    });

    overlay.addEventListener('pointermove', (event) => {
      move(event.clientX, event.clientY, event);
    });

    overlay.addEventListener(
      'touchmove',
      (event) => {
        if (!event.touches?.length) return;
        const touch = event.touches[0];
        if (!dragging) {
          begin(touch.clientX, touch.clientY, event);
          return;
        }
        move(touch.clientX, touch.clientY, event);
      },
      { passive: false }
    );

    overlay.addEventListener('pointerup', end);
    overlay.addEventListener('pointercancel', end);
    overlay.addEventListener('touchend', end, { passive: false });
  }

  function isCoarsePointer() {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  function enableZoom() {
    document.querySelectorAll('.image-magnify-hover').forEach((image) => {
      if (image.dataset.wodMagnifyBound === '1') return;
      image.dataset.wodMagnifyBound = '1';

      image.addEventListener('click', (event) => {
        if (performance.now() < suppressImageClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (activeOverlay && activeOverlay.parentElement === image.parentElement) {
          event.preventDefault();
          event.stopPropagation();
          removeOverlay({ animate: true });
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const overlay = createOverlay(image);
        if (isCoarsePointer() || event.pointerType === 'touch') {
          bindTouchPan(overlay, image, event);
        } else {
          bindDesktop(overlay, image, event);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enableZoom);
  } else {
    enableZoom();
  }

  document.addEventListener('shopify:section:load', enableZoom);
  document.addEventListener('variant:change', () => setTimeout(enableZoom, 50));
})();
